import React, {
  ChangeEvent,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { normalizeOfficeProperties } from "../../utils/NormalizationUtil.ts";
import OfficeService from "../../services/OfficeService/OfficeService.ts";
import TreeView from "../../components/TreeView/TreeView.tsx";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const NEW_OFFICE_ENTRY_OPTION = {
  "0": "Създай нов офис",
};
const NEW_OFFICE_VALUE = "0";

const SUCCESS_MESSAGE = "Успешен запис";
const ERROR_MESSAGE = "Неуспешен запис";
const DEFAULT_OFFICE_STATE = {
  divisionName: "",
  masterDivision: "",
  isActive: 0,
  agencies: [],
};

const FIELD_DIVISION_NAME = "divisionName";
const FIELD_IS_ACTIVE = "isActive";
const ACTIVE_STATUS = { YES: 1, NO: 0 };

const OfficeManagement = () => {
  const { jwt } = useAuth();
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [treeHeight, setTreeHeight] = useState<number | string>("auto");

  const [allOffices, setAllOffices] = useState<{ [key: string]: string }>({});
  const [officeData, setOfficeData] = useState(DEFAULT_OFFICE_STATE);
  const [selectedOfficeId, setSelectedOfficeId] =
    useState<string>(NEW_OFFICE_VALUE);
  const [activeOffices, setActiveOffices] = useState<{ [key: string]: string }>(
    {}
  );
  const [agenciesOptions, setAgenciesOptions] = useState<Option[]>([]);
  const [agencies, setAgencies] = useState<{ [key: number]: string }>({});

  const [officesTreeDisplay, setOfficesTreeDisplay] = useState<any>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgentValues, setSelectedAgentValues] = useState<string[]>([]);
  const [agenciesChanged, setAgenciesChanged] = useState(false);

  const handleSelectionChange = (selectedValues: string[]) => {
    setSelectedAgentValues(selectedValues);
    setAgenciesChanged(true);
  };

  // Filter out the currently selected office from master division options
  const filteredActiveOffices = useMemo(() => {
    const base = { 0: " ", ...activeOffices };
    if (selectedOfficeId === NEW_OFFICE_VALUE) return base;
    return Object.fromEntries(
      Object.entries(base).filter(([key]) => key !== selectedOfficeId)
    );
  }, [activeOffices, selectedOfficeId]);

  const displayAlert = (message: string, type: FadeAlertType) => {
    setAlert(message);
    setAlertType(type);
  };

  const fetchOfficesData = async () => {
    try {
      setIsLoading(true);
      const [officesAll, activeOffices, officeTree, agencies] =
        await Promise.all([
          InsuranceService.getAllOffices(jwt, true),
          InsuranceService.getAllOffices(jwt, false),
          OfficeService.getOffTree(jwt),
          InsuranceService.getAllInsuranceCompanies(jwt, true),
        ]);

      setAllOffices({
        ...NEW_OFFICE_ENTRY_OPTION,
        ...(officesAll as { [key: string]: string }),
      });
      setActiveOffices(activeOffices as { [key: string]: string });
      setOfficesTreeDisplay(officeTree);
      setAgencies(agencies as { [key: string]: string });
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTreeHeight = () => {
    if (inputContainerRef.current) {
      setTreeHeight(inputContainerRef.current.clientHeight);
    }
  };

  useEffect(() => {
    fetchOfficesData();
  }, [jwt]);

  useEffect(() => {
    updateTreeHeight();
  }, [allOffices, officeData]);

  useEffect(() => {
    const updatedAgentOptions = Object.entries(agencies).map(([id, name]) => {
      return {
        text: name,
        value: id,
        id: id,
        selected: false,
      };
    });
    setAgenciesOptions(updatedAgentOptions);
  }, [agencies]);

  const handleOfficeSelectChange = async (value: string, name: string) => {
    if (name === FIELD_DIVISION_NAME) {
      handleDivisionNameChange(value);
    } else {
      setOfficeData((prevData) => ({
        ...prevData,
        [name]: name === FIELD_IS_ACTIVE ? Number(value) : value,
      }));
    }
  };

  const handleDivisionNameChange = async (value: string) => {
    setSelectedOfficeId(value);
    if (value === NEW_OFFICE_VALUE) {
      resetOfficeData();
    } else {
      try {
        const officeDetails = await OfficeService.getOfficeById(jwt, value);
        const normalizedOfficeData = normalizeOfficeProperties(
          officeDetails as object
        );

        setOfficeData({
          ...normalizedOfficeData,
          isActive: normalizedOfficeData.isActive ? 1 : 0,
          selectedAgenciesValues: normalizedOfficeData.officeAgencies,
        });

        const agenciesArray =
          typeof normalizedOfficeData.officeAgencies === "string"
            ? normalizedOfficeData.officeAgencies.split(",")
            : normalizedOfficeData.officeAgencies ?? [];

        // Pre-populate selectedAgentValues with existing agencies
        setSelectedAgentValues(agenciesArray.map(String));
        setAgenciesChanged(false);

        const updatedAgenciesOptions = agenciesOptions.map((option) => {
          const isSelected = agenciesArray.includes(String(option.value));

          return {
            ...option,
            selected: isSelected,
          };
        });
        setAgenciesOptions(updatedAgenciesOptions);
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      }
    }
  };

  const resetOfficeData = () => {
    setOfficeData(DEFAULT_OFFICE_STATE);
    setSelectedOfficeId(NEW_OFFICE_VALUE);
    setSelectedAgentValues([]);
    setAgenciesChanged(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOfficeData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const officeDataToSave = normalizeOfficeProperties(officeData, true);

      const agenciesToSave = agenciesChanged
        ? selectedAgentValues.join(",")
        : selectedAgentValues.join(",");

      const payload = {
        ...officeDataToSave,
        IS_ACTIVE: officeData.isActive,
        ZK_AGS: agenciesToSave,
      };

      const response = await OfficeService.saveOffice(jwt, payload);

      if (response) {
        displayAlert(SUCCESS_MESSAGE, FadeAlertType.success);
        resetOfficeData();
        fetchOfficesData();
      } else {
        throw new Error(ERROR_MESSAGE);
      }
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    }
  };

  const memoizedTreeData = useMemo(
    () => officesTreeDisplay,
    [officesTreeDisplay]
  );

  return (
    <>
      <Breadcrumb pageName="Управление на офиси" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-4 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />
        <SectionHeader title="Управление на офиси" />
        <form onSubmit={handleSave} className="p-6.5">
          <div className="flex flex-col md:flex-row">
            <div ref={inputContainerRef} className="pr-4 md:w-2/3">
              <div className="mb-3 grid gap-6 sm:grid-cols-2">
                <div className="col-span-2">
                  {allOffices && (
                    <div className="w-full">
                      <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                        Офис
                      </label>
                      <SelectGroup
                        value={
                          officeData.divisionName === ""
                            ? NEW_OFFICE_VALUE
                            : Object.keys(allOffices).find(
                                (key) =>
                                  allOffices[key] === officeData.divisionName
                              ) || NEW_OFFICE_VALUE
                        }
                        placeholder="Изберете офис"
                        options={allOffices}
                        onChange={handleOfficeSelectChange}
                        name={FIELD_DIVISION_NAME}
                      />
                    </div>
                  )}
                </div>
                <div className="col-span-2 lg:col-span-2">
                  <label className={labelClass}>Име на Офиса</label>
                  <input
                    type="text"
                    name={FIELD_DIVISION_NAME}
                    value={officeData.divisionName}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                    Главно подразделение
                  </label>
                  <SelectGroup
                    value={officeData.masterDivision}
                    placeholder="Изберете офис"
                    options={filteredActiveOffices}
                    onChange={handleOfficeSelectChange}
                    name="masterDivision"
                  />
                </div>

                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                    Агенции
                  </label>
                  <MultiSelect
                    options={agenciesOptions}
                    id={String(Math.random())}
                    setOptions={setAgenciesOptions}
                    placeholder={"Изберете агенции"}
                    onSelectionChange={handleSelectionChange}
                  />
                </div>

                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                    Действащ
                  </label>
                  <SelectGroup
                    value={officeData.isActive.toString()}
                    options={{
                      [ACTIVE_STATUS.YES]: "Да",
                      [ACTIVE_STATUS.NO]: "Не",
                    }}
                    onChange={handleOfficeSelectChange}
                    name={FIELD_IS_ACTIVE}
                  />
                </div>
              </div>
            </div>
            <div className="border-primary md:w-1/3 md:pl-4">
              <div
                className="mt-8 md:mt-0"
                style={{ height: treeHeight, overflowY: "auto" }}
              >
                <div className="mb-4 text-sm font-medium text-black dark:text-white">
                  Дърво на Офисите
                </div>
                <div className="max-h-full">
                  <TreeView data={memoizedTreeData} isLoading={isLoading} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className="bg-primary text-gray mt-8 flex w-1/6 justify-center rounded p-3 font-medium hover:bg-opacity-90"
            >
              Запис
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default OfficeManagement;
